import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../../design-system/theme';
import { Icon } from '../../design-system/components/Icon';

interface NavItem {
  path: string;
  label: string;
  icon: string;
  children?: NavItem[];
}

const navItems: NavItem[] = [
  { path: '/admin', label: 'Home', icon: 'home' },
  { path: '/admin/orders', label: 'Orders', icon: 'orders' },
  { path: '/admin/products', label: 'Products', icon: 'products' },
  { path: '/admin/inventory', label: 'Inventory', icon: 'inventory' },
  { path: '/admin/categories', label: 'Categories', icon: 'categories' },
  { path: '/admin/collections', label: 'Collections', icon: 'collections' },
  { path: '/admin/customers', label: 'Customers', icon: 'customers' },
  { path: '/admin/discounts', label: 'Discounts', icon: 'discounts' },
  { path: '/admin/vouchers', label: 'Vouchers', icon: 'voucher' },
  { path: '/admin/shipping-rates', label: 'Shipping', icon: 'shipping' },
  { path: '/admin/analytics', label: 'Analytics', icon: 'analytics' },
  { path: '/admin/marketing', label: 'Marketing', icon: 'marketing' },
  { path: '/admin/online-store', label: 'Online Store', icon: 'store',
    children: [
      { path: '/admin/online-store/blog', label: 'Blog Posts', icon: 'blog' }
    ]
  },
  { path: '/admin/reviews', label: 'Reviews', icon: 'star' },

  { path: '/admin/sales-channels', label: 'Sales Channels', icon: 'channels' },
];

const SidebarContainer = styled.aside.withConfig({
  shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen: boolean }>`
  width: ${theme.sidebarWidth};
  background: ${theme.colors.bg};
  border-right: 1px solid ${theme.colors.border};
  position: fixed;
  left: 0;
  top: 0;
  height: 100vh;
  display: flex;
  flex-direction: column;
  z-index: 100;
  overflow-y: auto;
  transition: transform ${theme.transitions.base};

  @media (max-width: ${theme.breakpoints.md}) {
    transform: translateX(${props => (props.isOpen ? '0' : '-100%')});
    box-shadow: ${theme.shadows.lg};
  }
`;

const LogoSection = styled.div`
  padding: ${theme.spacing.lg};
  border-bottom: 1px solid ${theme.colors.border};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
`;

const Logo = styled.div`
  width: 32px;
  height: 32px;
  border-radius: ${theme.borderRadius.base};
  background: ${theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.textInverse};
  font-weight: ${theme.typography.fontWeight.bold};
  font-size: ${theme.typography.fontSize.lg};
`;

const BrandText = styled.div`
  display: flex;
  flex-direction: column;
`;

const BrandName = styled.div`
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.textPrimary};
  line-height: ${theme.typography.lineHeight.tight};
`;

const BrandSubtitle = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.textTertiary};
  line-height: ${theme.typography.lineHeight.tight};
`;

const Nav = styled.nav`
  flex: 1;
  padding: ${theme.spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

const NavItemLink = styled(Link).withConfig({
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.base};
  text-decoration: none;
  color: ${props => props.isActive ? theme.colors.textPrimary : theme.colors.textSecondary};
  font-weight: ${props => props.isActive ? theme.typography.fontWeight.semibold : theme.typography.fontWeight.normal};
  font-size: ${theme.typography.fontSize.base};
  transition: ${theme.transitions.fast};
  position: relative;
  
  ${props => props.isActive && `
    background: ${theme.colors.bgSecondary};
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 3px;
      background: ${theme.colors.primary};
      border-radius: 0 2px 2px 0;
    }
  `}
  
  &:hover {
    background: ${props => props.isActive ? theme.colors.bgSecondary : theme.colors.bgTertiary};
    color: ${theme.colors.textPrimary};
  }
`;

const SubNavItemLink = styled(Link).withConfig({
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.xs} ${theme.spacing.md};
  padding-left: ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.base};
  text-decoration: none;
  color: ${props => props.isActive ? theme.colors.textPrimary : theme.colors.textTertiary};
  font-weight: ${props => props.isActive ? theme.typography.fontWeight.medium : theme.typography.fontWeight.normal};
  font-size: ${theme.typography.fontSize.sm};
  transition: ${theme.transitions.fast};
  
  ${props => props.isActive && `
    color: ${theme.colors.textPrimary};
    font-weight: ${theme.typography.fontWeight.medium};
  `}
  
  &:hover {
    background: ${theme.colors.bgTertiary};
    color: ${theme.colors.textPrimary};
  }
`;

const SettingsSection = styled.div`
  padding: ${theme.spacing.md};
  border-top: 1px solid ${theme.colors.border};
`;

const SettingsLink = styled(Link).withConfig({
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.base};
  text-decoration: none;
  color: ${props => props.isActive ? theme.colors.textPrimary : theme.colors.textSecondary};
  font-weight: ${props => props.isActive ? theme.typography.fontWeight.semibold : theme.typography.fontWeight.normal};
  font-size: ${theme.typography.fontSize.base};
  transition: ${theme.transitions.fast};
  position: relative;
  
  ${props => props.isActive && `
    background: ${theme.colors.bgSecondary};
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 3px;
      background: ${theme.colors.primary};
      border-radius: 0 2px 2px 0;
    }
  `}
  
  &:hover {
    background: ${props => props.isActive ? theme.colors.bgSecondary : theme.colors.bgTertiary};
    color: ${theme.colors.textPrimary};
  }
`;

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ isOpen = true, onClose }) => {
  const location = useLocation();

  return (
    <SidebarContainer isOpen={isOpen}>
      <LogoSection>
        <Logo>S</Logo>
        <BrandText>
          <BrandName>Seenotty</BrandName>
          <BrandSubtitle>Admin</BrandSubtitle>
        </BrandText>
      </LogoSection>
      
      <Nav>
        {navItems.map(item => {
          const isActive: boolean = location.pathname === item.path || 
            (item.children ? item.children.some(child => location.pathname === child.path) : false);
          
          if (item.children && item.children.length > 0) {
            return (
              <React.Fragment key={item.path}>
                <NavItemLink
                  to={item.path}
                  isActive={isActive}
                  onClick={onClose}
                >
                  <Icon name={item.icon} size={20} />
                  <span>{item.label}</span>
                </NavItemLink>
                {item.children.map(child => (
                  <SubNavItemLink
                    key={child.path}
                    to={child.path}
                    isActive={location.pathname === child.path}
                    onClick={onClose}
                  >
                    <Icon name={child.icon} size={16} />
                    <span>{child.label}</span>
                  </SubNavItemLink>
                ))}
              </React.Fragment>
            );
          }
          
          return (
            <NavItemLink
              key={item.path}
              to={item.path}
              isActive={isActive}
              onClick={onClose}
            >
              <Icon name={item.icon} size={20} />
              <span>{item.label}</span>
            </NavItemLink>
          );
        })}
      </Nav>
      
      <SettingsSection>
        <SettingsLink
          to="/admin/settings"
          isActive={location.pathname === '/admin/settings'}
          onClick={onClose}
        >
          <Icon name="settings" size={20} />
          <span>Settings</span>
        </SettingsLink>
      </SettingsSection>
    </SidebarContainer>
  );
};

export default Sidebar;

